import { __awaiter } from "tslib";
import * as React from 'react';
import i18next from 'i18next';
import { WebskyEventType } from '../types';
import { addExternalResource } from './utils';
import { emitEvent } from '../utils';
const addResources = (i18n, resource) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        if (!i18n) {
            return ['I18N library is not initialized'];
        }
        if (typeof resource !== 'object') {
            return ['Locales should only be of `object` type'];
        }
        return yield addExternalResource(i18n, resource);
    }
    catch (e) {
        return [e];
    }
});
/**
 * Component for support third party (external) translation resources.
 * External js script must subscribe on `WebskyEventType.LanguageChanged` event.
 * It is emitted after i18next was initialized or locale of Websky's engine was changed,
 * and indicated that `__webskyAddLocaleResource` can be used.
 *
 * `__webskyAddLocaleResource` is global function that can pass bundles from external resources to i18next instance.
 * It takes an argument in the following format:
 *
 * If MODULE_NAME is not specified, translation will be placed into default bundle
 *
 * ```
 * 	{
 * 	   [LANG_KEY]: {
 * 	       '[MODULE_NAME]:[KEY]': '[TRANSLATION]'
 * 	   }
 * 	}
 * ```
 */
const ExternalLocaleResources = () => {
    const initEvent = React.useRef(false);
    const externalResource = React.useRef(null);
    React.useEffect(() => {
        i18next.on('initialized', () => {
            if (externalResource.current) {
                addResources(i18next, externalResource.current).then(errors => {
                    if (errors.length) {
                        console.log(...['External locales errors:', ...errors]);
                    }
                    externalResource.current = null;
                    i18next.changeLanguage(i18next.language);
                    console.info(`External locales added`);
                });
            }
        });
        i18next.on('languageChanged', lang => {
            emitEvent(document, {
                type: WebskyEventType.LanguageChanged,
                data: lang
            });
        });
        window['__webskyAddLocaleResource'] = (resource) => {
            externalResource.current = resource;
        };
        if (!initEvent.current) {
            emitEvent(document, {
                type: WebskyEventType.LanguageChanged,
                data: i18next.language
            });
            initEvent.current = true;
        }
        return () => {
            initEvent.current = false;
        };
    }, []);
    return null;
};
export default ExternalLocaleResources;
